import React, { useEffect, useMemo, useState } from "react";
import { Users, Mail, Phone, MapPin, ShoppingBag, Wallet } from "lucide-react";
import { api } from "../../lib/api";
import { SearchBox, Modal, Stat } from "./ui";

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  useEffect(() => { api.get("/admin/customers").then((r) => setRows(r.data)).catch(() => {}); }, []);

  const list = useMemo(() => rows.filter((c) =>
    !q || `${c.name} ${c.email} ${c.phone}`.toLowerCase().includes(q.toLowerCase())), [rows, q]);
  const totalRevenue = rows.reduce((s, c) => s + c.total_spent_gbp, 0);

  return (
    <div className="space-y-5" data-testid="admin-customers">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat icon={Users} label="Customers" value={rows.length} />
        <Stat icon={Wallet} label="Total revenue" value={`GH₵ ${Math.round(totalRevenue * 19.5).toLocaleString()}`} sub={`£${totalRevenue.toLocaleString()}`} />
        <Stat icon={ShoppingBag} label="Repeat buyers" value={rows.filter((c) => c.paid_orders > 1).length} />
      </div>
      <SearchBox value={q} onChange={setQ} placeholder="Search customers…" />
      <div className="bg-white border border-black/5 rounded-2xl overflow-hidden divide-y divide-black/5">
        {list.length === 0 && <p className="p-5 text-[#5C5C5C]">No customers yet.</p>}
        {list.map((c) => (
          <button key={c.email} onClick={() => setSel(c)} data-testid={`customer-${c.email}`}
            className="w-full text-left p-4 flex items-center gap-4 hover:bg-black/[0.02]">
            <span className="w-10 h-10 rounded-full bg-[#0D530E] text-[#FBF5DD] flex items-center justify-center font-display shrink-0">{(c.name || "?")[0]?.toUpperCase()}</span>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[#0D530E] truncate">{c.name} {c.paid_orders > 1 && <span className="text-[10px] bg-[#E7E1B1] text-[#0D530E] rounded-full px-2 py-0.5 ml-1">repeat</span>}</p>
              <p className="text-xs text-[#5C5C5C] truncate">{c.email} · {c.cities.join(", ") || "—"}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="font-display text-lg text-[#306D29]">GH₵ {c.total_spent_ghs.toLocaleString()}</p>
              <p className="text-xs text-[#5C5C5C]">{c.paid_orders} paid · {c.orders} total</p>
            </div>
          </button>
        ))}
      </div>

      {sel && (
        <Modal title={sel.name} onClose={() => setSel(null)} testid="customer-detail">
          <div className="space-y-4">
            <div className="flex flex-wrap gap-4 text-sm">
              <a href={`mailto:${sel.email}`} className="inline-flex items-center gap-1.5 text-[#306D29] font-medium"><Mail size={15} /> {sel.email}</a>
              {sel.phone && <a href={`https://wa.me/${sel.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#306D29] font-medium"><Phone size={15} /> {sel.phone}</a>}
              {sel.cities.length > 0 && <span className="inline-flex items-center gap-1.5 text-[#5C5C5C]"><MapPin size={15} /> {sel.cities.join(", ")}</span>}
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-[#E7E1B1]/50 rounded-xl p-3"><p className="font-display text-xl text-[#0D530E]">GH₵ {sel.total_spent_ghs.toLocaleString()}</p><p className="text-xs text-[#5C5C5C]">Lifetime value</p></div>
              <div className="bg-[#E7E1B1]/50 rounded-xl p-3"><p className="font-display text-xl text-[#0D530E]">{sel.paid_orders}</p><p className="text-xs text-[#5C5C5C]">Paid orders</p></div>
              <div className="bg-[#E7E1B1]/50 rounded-xl p-3"><p className="font-display text-xl text-[#0D530E]">{sel.orders}</p><p className="text-xs text-[#5C5C5C]">Total orders</p></div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-[#5C5C5C] mb-2">Order history</p>
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {sel.order_list.map((o) => (
                  <div key={o.order_number} className="flex justify-between items-center text-sm bg-white border border-black/5 rounded-lg px-3 py-2">
                    <span className="font-mono text-[#0D530E]">{o.order_number}</span>
                    <span className="text-[#5C5C5C]">{(o.created_at || "").slice(0, 10)}</span>
                    <span className={`text-xs font-semibold ${o.payment_status === "paid" ? "text-[#2D7A5D]" : "text-[#D9933B]"}`}>{o.payment_status}</span>
                    <span className="font-semibold text-[#306D29]">GH₵ {(o.total_ghs || 0).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
